<template>
  <main class="performance-view">
    <header class="page-header">
      <div>
        <p class="eyebrow">Systemdiagnose · nur im Arbeitsspeicher</p>
        <h1>Performance</h1>
        <p>Langsame Endpoints und gemeinsame Engpässe erkennen.</p>
      </div>
      <button class="toggle" :disabled="busy || !data || data.locked" @click="toggle">
        {{ data && data.enabled ? 'Messung ausschalten' : 'Messung einschalten' }}
      </button>
    </header>

    <p v-if="error" class="notice error" role="alert">{{ error }}</p>
    <p v-if="instanceChanged" class="notice" role="status">Der Backend-Prozess hat gewechselt. Diese Messwerte gehören nur zum aktuell antwortenden Prozess; sie sind keine gemeinsame Historie mehrerer Instanzen.</p>
    <section v-if="data" class="status-panel" aria-label="Messstatus">
      <span class="badge" :class="{ active: data.enabled }">{{ data.enabled ? 'Messung aktiv' : 'Messung aus' }}</span>
      <span>Serverweit · Prozess {{ data.processId }} · Stand {{ time(data.generatedAt, true) }}</span>
      <span v-if="data.startedAt">Seit {{ time(data.startedAt, true) }}</span>
      <p v-if="data.locked && !data.fault">Die Server-Einstellung PERFORMANCE_MONITOR_ENABLED deaktiviert die Messung dauerhaft.</p>
      <p v-else-if="!data.enabled && !data.locked">Es werden keine neuen Messwerte gesammelt. Beim Einschalten beginnt eine neue Messung.</p>
      <p v-if="data.fault" class="error">{{ data.fault }}</p>
      <p>Maximal 60 Minuten im RAM. Ausschalten und Neustart löschen die Messwerte. Der Schalter gilt für alle Nutzer dieses Backend-Prozesses.</p>
    </section>

    <div class="toolbar">
      <label>Zeitraum
        <select v-model.number="minutes" :disabled="busy" @change="changeWindow">
          <option :value="15">Letzte 15 Minuten</option>
          <option :value="60">Letzte 60 Minuten</option>
        </select>
      </label>
      <button :disabled="busy" @click="refresh">{{ busy ? 'Wird geladen …' : 'Aktualisieren' }}</button>
      <label class="check"><input v-model="auto" type="checkbox" /> Alle 15 Sekunden aktualisieren</label>
    </div>

    <template v-if="data">
      <p class="scope">{{ selectedMinute === null ? 'Auswertung für den gewählten Zeitraum' : 'Abgeschlossene Requests in der Minute ab ' + time(selectedMinute) }}</p>
      <section class="metrics" aria-label="Kennzahlen">
        <article><span>Abgeschlossene Requests</span><strong>{{ number(data.summary.count) }}</strong></article>
        <article><span>P95, ungefähr</span><strong>{{ duration(data.summary.p95Ms) }}</strong></article>
        <article><span>Ab 1 Sekunde</span><strong>{{ number(data.summary.slow) }}</strong></article>
        <article><span>Fehler / Abbrüche</span><strong>{{ number(data.summary.errors) }} / {{ number(data.summary.aborted) }}</strong></article>
        <article><span>Jetzt laufend · gesamter Prozess</span><strong>{{ number(data.inFlight) }}</strong></article>
      </section>

      <section class="panel trend-panel" aria-labelledby="trend-heading">
        <div class="section-header">
          <div><p class="eyebrow">Minute für Minute</p><h2 id="trend-heading">Wann wurde es langsam?</h2></div>
          <label class="chart-picker">Anzeigen
            <select v-model="chartMetric">
              <option value="lagMaxMs">Loop-Verzögerung</option>
              <option value="count">Requests</option>
              <option value="cpuMax">CPU-Auslastung</option>
            </select>
          </label>
        </div>
        <p class="trend-intro">Einen Balken auswählen, um die abgeschlossenen Requests dieser Minute in der Endpoint-Tabelle zu sehen.</p>

        <template v-if="chartRows.length">
          <div class="trend-summary">
            <span class="trend-status" :class="{ elevated: lagPeaks.count }"><i aria-hidden="true"></i>{{ lagPeaks.count ? number(lagPeaks.count) + (lagPeaks.count === 1 ? ' auffällige Minute' : ' auffällige Minuten') : lagPeaks.top ? 'Keine erhöhte Loop-Verzögerung gemessen' : 'Erste Messung steht noch aus' }}</span>
            <button v-if="lagPeaks.top" class="peak-button" :disabled="busy" @click="selectMinute(lagPeaks.top.at)">Höchster Loop-Wert: <b>{{ duration(lagPeaks.top.lagMaxMs) }}</b> um {{ time(lagPeaks.top.at) }} <span aria-hidden="true">↗</span></button>
          </div>

          <div class="chart-legend"><span><i class="legend-mark" aria-hidden="true"></i>{{ chartLabel }}</span><span v-if="chartMetric === 'lagMaxMs'"><i class="legend-mark orange" aria-hidden="true"></i>Auffällig ab 200 ms</span><span><i class="legend-mark hollow" aria-hidden="true"></i>Keine Messung</span></div>
          <div class="chart-layout">
            <div class="chart-axis" aria-hidden="true"><span>{{ chartValue(chartCeiling) }}</span><span>{{ chartValue(chartCeiling / 2) }}</span><span>0</span></div>
            <div class="chart-scroll" tabindex="0" role="group" aria-label="Minutenverlauf, bei Bedarf seitlich scrollen">
              <div class="chart-canvas" :style="{ '--minute-count': chartRows.length }">
                <div class="chart-plot">
                  <div class="chart-grid" aria-hidden="true"><i></i><i></i><i></i></div>
                  <div v-if="chartMetric === 'lagMaxMs'" class="threshold-line" :style="{ bottom: (200 / chartCeiling * 100) + '%' }" aria-hidden="true"></div>
                  <div class="chart-bars" role="group" aria-label="Minute auswählen">
                    <button v-for="row in chartRows" :key="row.at" class="chart-minute"
                      :class="{ selected: selectedMinute === row.at, warning: chartMetric === 'lagMaxMs' && row.lagMaxMs >= 200, missing: !hasChartValue(row) }"
                      :disabled="busy || row.missing" :aria-pressed="selectedMinute === row.at" :aria-label="chartTitle(row)" :title="chartTitle(row)" @click="selectMinute(row.at)">
                      <span v-if="hasChartValue(row)" class="chart-bar" :style="{ height: barHeight(row) + '%' }" aria-hidden="true"></span>
                      <span v-else class="missing-mark" aria-hidden="true"></span>
                      <span v-if="selectedMinute === row.at" class="selection-marker" aria-hidden="true">●</span>
                    </button>
                  </div>
                </div>
                <div class="chart-times" aria-hidden="true"><span v-for="tick in chartTicks" :key="tick.at" :style="{ gridColumn: tick.index + 1 }" :class="{ first: tick.index === 0, last: tick.index === chartRows.length - 1 }">{{ time(tick.at) }}</span></div>
              </div>
            </div>
          </div>
          <p class="chart-scroll-hint">Auf kleinen Bildschirmen lässt sich der Verlauf seitlich verschieben.</p>
        </template>
        <p v-else class="empty">Sobald die Messung läuft, erscheint hier der Minutenverlauf.</p>

        <section v-if="focusSample" class="minute-detail" aria-label="Details zur Minute">
          <div class="detail-heading">
            <div><span class="detail-caption">{{ selectedMinute === null ? 'Neueste Minute' : 'Ausgewählte Minute · Tabelle gefiltert' }}</span><h3>{{ time(focusSample.at) }} <span class="detail-live" v-if="isCurrentMinute(focusSample.at)">Läuft noch</span></h3></div>
            <button v-if="selectedMinute !== null" :disabled="busy" @click="selectMinute(null)">Auswahl aufheben</button>
            <span v-else class="detail-caption">Die Endpoint-Tabelle zeigt den gesamten Zeitraum.</span>
          </div>
          <div class="detail-metrics">
            <article :class="{ elevated: focusSample.samples && focusSample.lagMaxMs >= 200 }"><span>Loop-Verzögerung</span><strong>{{ focusSample.samples ? duration(focusSample.lagMaxMs) : '–' }}</strong><small>{{ !focusSample.samples ? 'Noch keine Messung' : focusSample.lagMaxMs >= 200 ? 'Erhöht · ab 200 ms' : 'Unter der 200-ms-Marke' }}</small></article>
            <article><span>Abgeschlossene Requests</span><strong>{{ number(focusSample.count) }}</strong><small>{{ number(focusSample.slow) }} davon ab 1 Sekunde</small></article>
            <article><span>Gleichzeitig laufend</span><strong>{{ focusSample.samples ? number(focusSample.peak) : '–' }}</strong><small>Höchster gemessener Wert</small></article>
            <article><span>CPU-Auslastung</span><strong>{{ focusSample.samples ? number(focusSample.cpuMax) + ' %' : '–' }}</strong><small>Maximum · bezogen auf einen Kern</small></article>
            <article><span>Arbeitsspeicher</span><strong>{{ focusSample.samples ? memory(focusSample.rssMax) : '–' }}</strong><small>Maximum im Backend-Prozess</small></article>
          </div>
        </section>
        <p class="trend-note">Auffällige Minuten zeigen gemeinsame Verzögerungen, aber noch keinen bewiesenen Verursacher. Die Reihenfolge einzelner Requests wird nicht erfasst.</p>
      </section>

      <section class="panel">
        <div class="section-header"><h2>Endpoints</h2><label>Suchen <input v-model.trim="search" type="search" placeholder="Endpoint filtern" /></label></div>
        <div class="table-wrap">
          <table>
            <thead><tr>
              <th scope="col">Endpoint</th>
              <th v-for="column in columns" :key="column.key" scope="col" :aria-sort="sort === column.key ? 'descending' : 'none'">
                <button @click="sort = column.key">{{ column.label }}{{ sort === column.key ? ' ↓' : '' }}</button>
              </th>
            </tr></thead>
            <tbody><tr v-for="row in visibleRoutes" :key="row.name">
              <th scope="row">{{ row.name }}</th><td>{{ number(row.count) }}</td><td>{{ duration(row.p95Ms) }}</td>
              <td>{{ duration(row.maxMs) }}</td><td>{{ duration(row.totalMs) }}</td><td>{{ number(row.errors) }}</td><td>{{ number(row.aborted) }}</td>
            </tr></tbody>
          </table>
        </div>
        <p v-if="!visibleRoutes.length" class="empty">{{ search ? 'Keine passenden Endpoints.' : 'In diesem Zeitraum wurden noch keine Requests abgeschlossen.' }}</p>
        <p v-if="filteredRoutes.length > limit">{{ limit }} von {{ filteredRoutes.length }} Endpoints angezeigt. Die Suche berücksichtigt alle Einträge. <button @click="limit += 50">Weitere anzeigen</button></p>
        <p v-if="data.routeLimitReached" class="notice">Das Limit von {{ data.maxRoutes }} Routengruppen wurde erreicht. Weitere Gruppen werden unter OTHER /[overflow] zusammengefasst.</p>
      </section>

      <section class="explanation">
        <h2>Was die Messung aussagt</h2>
        <p>Ein langsamer Endpoint kann auf TAC oder die Datenbank warten, ohne andere Requests zu blockieren. Steigen gleichzeitig Loop-Verzögerung und laufende Requests, ist das ein Hinweis auf einen gemeinsamen Engpass. Die Tabelle zeigt zeitgleiche Kandidaten, keine bewiesenen Verursacher.</p>
        <p>Requests werden ihrer Abschlussminute zugeordnet. Lange Requests können früher begonnen haben; noch laufende Requests erscheinen erst nach Abschluss in der Tabelle. Hintergrundjobs und Socket-Aktionen können ebenfalls Last verursachen, werden hier aber nicht einzeln gemessen.</p>
        <p>P95 ist die obere Grenze einer Laufzeitklasse und deshalb ungefähr. Fehler zählen HTTP 4xx/5xx; Abbrüche werden separat gezählt. Die aufsummierte Request-Zeit enthält Wartezeit und parallele Requests und ist kein CPU-Verbrauch. Die Loop-Messung hat eine Grundauflösung von etwa 50 ms. Wartezeiten vor Node sowie Netzwerk und Browser sind nicht enthalten.</p>
      </section>
    </template>
  </main>
</template>

<script>
import api from "@/api/performanceApi"

export default {
  data() {
    return { data: null, error: "", busy: false, auto: true, minutes: 60, selectedMinute: null,
      search: "", sort: "p95Ms", chartMetric: "lagMaxMs", limit: 50, timer: null, disposed: false, instanceChanged: false,
      columns: [{ key: "count", label: "Anzahl" }, { key: "p95Ms", label: "P95 ≈" },
        { key: "maxMs", label: "Maximum" }, { key: "totalMs", label: "Request-Zeit gesamt" },
        { key: "errors", label: "Fehler" }, { key: "aborted", label: "Abbrüche" }] }
  },
  computed: {
    filteredRoutes() {
      const term = this.search.toLowerCase()
      return (this.data ? this.data.routes : []).filter((row) => row.name.toLowerCase().includes(term))
        .sort((a, b) => b[this.sort] - a[this.sort] || b.count - a.count || a.name.localeCompare(b.name))
    },
    visibleRoutes() { return this.filteredRoutes.slice(0, this.limit) },
    focusSample() {
      if (!this.data) return null
      return this.data.timeline.find((row) => row.at === this.selectedMinute) || this.data.timeline[this.data.timeline.length - 1]
    },
    chartRows() {
      const rows = this.data ? this.data.timeline : []
      if (!rows.length) return []
      const end = rows[rows.length - 1].at
      const start = Math.max(rows[0].at, end - (this.minutes - 1) * 60000)
      const byMinute = new Map(rows.map((row) => [row.at, row]))
      const result = []
      for (let at = start; at <= end; at += 60000) result.push(byMinute.get(at) || { at, missing: true })
      return result
    },
    chartLabel() {
      return { lagMaxMs: "Maximale Loop-Verzögerung", count: "Abgeschlossene Requests", cpuMax: "Maximale CPU-Auslastung · % eines Kerns" }[this.chartMetric]
    },
    chartCeiling() {
      const values = this.chartRows.filter((row) => this.hasChartValue(row)).map((row) => row[this.chartMetric])
      const floor = { lagMaxMs: 250, count: 5, cpuMax: 100 }[this.chartMetric]
      const target = Math.max(floor, ...values)
      const step = Math.pow(10, Math.floor(Math.log10(target)))
      return [1, 2, 2.5, 5, 10].map((value) => value * step).find((value) => value >= target)
    },
    chartTicks() {
      const last = this.chartRows.length - 1
      if (last < 0) return []
      const indexes = new Set([0, last])
      if (last >= 8) for (let index = 1; index < 4; index++) indexes.add(Math.round(last * index / 4))
      return Array.from(indexes).sort((a, b) => a - b).map((index) => ({ at: this.chartRows[index].at, index }))
    },
    lagPeaks() {
      const rows = this.chartRows.filter((row) => !row.missing && row.samples > 0)
      return { count: rows.filter((row) => row.lagMaxMs >= 200).length,
        top: rows.reduce((top, row) => !top || row.lagMaxMs > top.lagMaxMs ? row : top, null) }
    },
  },
  mounted() {
    this.disposed = false
    this.refresh()
    this.timer = setInterval(() => {
      if (this.auto && !document.hidden && (!this.data || this.data.enabled)) this.refresh()
    }, 15000)
  },
  beforeDestroy() { this.disposed = true; clearInterval(this.timer) },
  methods: {
    hasChartValue(row) { return !row.missing && (this.chartMetric === "count" || row.samples > 0) && Number.isFinite(row[this.chartMetric]) },
    chartValue(value) { return this.chartMetric === "lagMaxMs" ? this.duration(value) : value.toLocaleString("de-CH", { maximumFractionDigits: 1 }) + (this.chartMetric === "cpuMax" ? " %" : "") },
    barHeight(row) { return row[this.chartMetric] > 0 ? Math.max(1, row[this.chartMetric] / this.chartCeiling * 100) : 0 },
    chartTitle(row) {
      if (!this.hasChartValue(row)) return this.time(row.at) + " · Keine Messung"
      return this.time(row.at) + " · " + this.chartLabel + ": " + this.chartValue(row[this.chartMetric]) + " · " + this.number(row.count) + " abgeschlossene Requests" + (this.isCurrentMinute(row.at) ? " · Minute läuft noch" : "")
    },
    isCurrentMinute(at) { return this.data && at === Math.floor(this.data.generatedAt / 60000) * 60000 },
    number(value) { return Math.round(value || 0).toLocaleString("de-CH") },
    duration(value) {
      if (value >= 60000) return (value / 60000).toLocaleString("de-CH", { maximumFractionDigits: 1 }) + " min"
      if (value >= 1000) return (value / 1000).toLocaleString("de-CH", { maximumFractionDigits: 2 }) + " s"
      return this.number(value) + " ms"
    },
    memory(value) { return this.number(value / 1024 / 1024) + " MB" },
    time(value, seconds = false) { return new Date(value).toLocaleTimeString("de-CH", { timeZone: "Europe/Zurich", hour: "2-digit", minute: "2-digit", ...(seconds ? { second: "2-digit" } : {}) }) },
    accept(value) {
      if (this.data && this.data.instanceId !== value.instanceId) this.instanceChanged = true
      this.data = value
    },
    async refresh() {
      if (this.busy || this.disposed) return
      const end = Math.floor(Date.now() / 60000) * 60000
      if (this.selectedMinute !== null && this.selectedMinute < end - (this.minutes - 1) * 60000) this.selectedMinute = null
      this.busy = true
      try {
        const data = await api.fetchPerformance({ minutes: this.minutes, ...(this.selectedMinute !== null ? { minute: this.selectedMinute } : {}) })
        if (!this.disposed) { this.accept(data); this.error = "" }
      } catch (error) {
        if (!this.disposed) {
          const status = error.response && error.response.status
          if (status === 503) {
            this.data = null
            this.auto = false
            this.error = "Performance-Messung deaktiviert oder nicht verfügbar. Das Backend kann ohne Messung weiterlaufen."
          } else this.error = [401, 403].includes(status) ? "Bitte Anmeldung und Berechtigung g_performance prüfen." : "Messwerte konnten nicht geladen werden. Angezeigte Werte sind möglicherweise veraltet."
        }
      } finally { this.busy = false }
    },
    async toggle() {
      if (this.busy || !this.data) return
      this.busy = true
      try {
        const data = await api.setPerformanceEnabled(!this.data.enabled)
        if (!this.disposed) { this.selectedMinute = null; this.minutes = 60; this.accept(data); this.error = "" }
      } catch (error) {
        if (!this.disposed) this.error = (error.response && error.response.data && error.response.data.message) || "Messstatus konnte nicht geändert werden. Bitte aktualisieren."
      } finally { this.busy = false }
    },
    changeWindow() { this.selectedMinute = null; this.limit = 50; this.refresh() },
    selectMinute(value) { this.selectedMinute = value; this.limit = 50; this.refresh() },
  },
}
</script>

<style scoped>
.performance-view { max-width: 90rem; margin: 0 auto; padding: 1.5rem; color: #20362c; font-size: 1rem; line-height: 1.55; }
.performance-view * { box-sizing: border-box; }
h1 { font-size: 2rem; line-height: 1.2; margin: .25rem 0 .5rem; }
h2 { font-size: 1.25rem; margin: 0; }
p { margin: .5rem 0; }
.page-header, .section-header, .toolbar, .status-panel { display: flex; flex-wrap: wrap; align-items: center; gap: 1rem; }
.page-header, .section-header { justify-content: space-between; }
.eyebrow, .scope, .status-panel, .panel > p, .explanation, .check { font-size: .875rem; }
.eyebrow { color: #466254; }
button, select, input { font: inherit; }
button, select, input[type="search"] { border: 1px solid #bacbc1; border-radius: .5rem; padding: .6rem .8rem; background: white; color: #20362c; }
button { cursor: pointer; }
button:disabled { cursor: default; opacity: .55; }
button:focus-visible, input:focus-visible, select:focus-visible { outline: 3px solid #438a6a; outline-offset: 2px; }
.toggle { background: #244e3a; color: white; border-color: #244e3a; }
.status-panel { margin: 1.5rem 0; background: #edf3ee; padding: 1rem; border-radius: .75rem; }
.status-panel p { flex-basis: 100%; margin: 0; }
.badge { padding: .2rem .65rem; border-radius: 1rem; background: #dfe4e0; font-size: .75rem; font-weight: 700; }
.badge.active { background: #cee9d8; color: #164629; }
.toolbar { margin-bottom: 1.25rem; }
label { display: flex; flex-wrap: wrap; align-items: center; gap: .5rem; font-size: .875rem; }
input[type="checkbox"] { width: 1.1rem; height: 1.1rem; }
.metrics { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 12rem), 1fr)); gap: .75rem; }
.metrics article { border: 1px solid #dbe5de; border-radius: .75rem; padding: 1rem; background: white; }
.metrics span { display: block; font-size: .875rem; }
.metrics strong { display: block; font-size: 1.6rem; margin-top: .5rem; overflow-wrap: anywhere; }
.panel { margin-top: 1.5rem; border: 1px solid #dbe5de; border-radius: .75rem; padding: 1rem; background: white; }
.trend-panel { padding: 1.25rem; }
.trend-panel .eyebrow { margin: 0 0 .2rem; font-size: .75rem; font-weight: 600; letter-spacing: .04em; }
.trend-intro { color: #52665a; max-width: 52rem; }
.chart-picker select { max-width: 100%; }
.trend-summary { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: .75rem; margin: 1.25rem 0; }
.trend-status { display: inline-flex; align-items: center; gap: .5rem; font-size: .875rem; }
.trend-status i { width: .5rem; height: .5rem; border-radius: 50%; background: #497d5b; flex-shrink: 0; }
.trend-status.elevated { color: #81500d; }
.trend-status.elevated i { background: #b76b13; }
.peak-button { font-size: .875rem; border-color: #dbe5de; background: #f5f8f5; }
.chart-legend { display: flex; flex-wrap: wrap; gap: .5rem 1.25rem; color: #52665a; margin-bottom: 1rem; font-size: .75rem; }
.chart-legend > span { display: inline-flex; align-items: center; gap: .4rem; }
.legend-mark { display: inline-block; width: .65rem; height: .65rem; border-radius: .15rem; background: #5a8069; }
.legend-mark.orange { background: #c18029; }
.legend-mark.hollow { background: white; border: 1px dashed #81978a; }
.chart-layout { display: grid; grid-template-columns: 4.25rem minmax(0, 1fr); gap: .5rem; }
.chart-axis { height: 12.5rem; display: flex; flex-direction: column; justify-content: space-between; text-align: right; font-size: .75rem; color: #52665a; line-height: 1; }
.chart-scroll { overflow-x: auto; padding: .2rem .2rem .5rem; }
.chart-scroll:focus-visible { outline: 2px solid #438a6a; outline-offset: 2px; }
.chart-canvas { min-width: max(20rem, calc(var(--minute-count) * .9rem)); }
.chart-plot { position: relative; height: 12rem; }
.chart-grid { position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: space-between; pointer-events: none; }
.chart-grid i { border-top: 1px solid #e2eae5; width: 100%; }
.threshold-line { position: absolute; inset-inline: 0; border-top: 1px dashed #bd7b28; pointer-events: none; }
.chart-bars { position: relative; display: grid; grid-template-columns: repeat(var(--minute-count), minmax(0, 1fr)); gap: .2rem; height: 100%; }
.chart-minute { position: relative; min-width: 0; border: 0; border-radius: .3rem .3rem 0 0; padding: 0; height: 100%; display: flex; align-items: flex-end; justify-content: center; background: transparent; }
.chart-minute:hover:not(:disabled) { background: #244e3a0a; }
.chart-minute:focus-visible { outline-offset: 0; z-index: 1; }
.chart-minute.selected { background: #e6efe8; box-shadow: inset 0 0 0 1px #315c42; }
.chart-bar { width: 70%; max-width: 2.5rem; background: #5a8069; border-radius: .2rem .2rem 0 0; pointer-events: none; }
.chart-minute.warning .chart-bar { background: #c18029; }
.chart-minute.selected .chart-bar { background: #244e3a; }
.chart-minute.selected.warning .chart-bar { background: #9f5b0e; }
.chart-minute.missing { background: repeating-linear-gradient(135deg, transparent, transparent 5px, #82988a12 5px, #82988a12 6px); }
.missing-mark { border: 1px dashed #81978a; height: .25rem; width: 70%; }
.selection-marker { position: absolute; top: .25rem; left: 0; right: 0; font-size: .75rem; line-height: 1; color: #244e3a; }
.chart-times { display: grid; grid-template-columns: repeat(var(--minute-count), minmax(0, 1fr)); gap: .2rem; padding-top: .5rem; font-size: .75rem; color: #52665a; }
.chart-times span { justify-self: center; white-space: nowrap; }
.chart-times .first { justify-self: start; }
.chart-times .last { justify-self: end; }
.chart-times .first.last { justify-self: center; }
.chart-scroll-hint { display: none; }
.minute-detail { margin-top: 1rem; padding: 1rem; border-radius: .6rem; background: #f3f7f3; }
.detail-heading { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: .75rem; margin-bottom: 1rem; }
.detail-caption { font-size: .875rem; color: #52665a; }
.detail-heading h3 { display: flex; flex-wrap: wrap; gap: .75rem; align-items: center; margin: .15rem 0 0; font-size: 1.25rem; }
.detail-live { font-size: .75rem; font-weight: 500; color: #52665a; border: 1px solid #cbd9cd; border-radius: 1rem; padding: .15rem .5rem; }
.detail-heading button { font-size: .875rem; }
.detail-metrics { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 9rem), 1fr)); gap: .75rem; }
.detail-metrics article { min-width: 0; padding: .75rem; background: white; border: 1px solid #e0e9e0; border-radius: .5rem; }
.detail-metrics article.elevated { border-color: #d5ae6f; background: #fff6e9; }
.detail-metrics span { display: block; font-size: .875rem; }
.detail-metrics strong { display: block; font-size: 1.4rem; margin: .4rem 0; overflow-wrap: anywhere; }
.detail-metrics small { display: block; font-size: .75rem; color: #52665a; }
.trend-note { color: #52665a; margin-top: 1rem; }
.table-wrap { overflow-x: auto; margin-top: 1rem; }
table { width: 100%; border-collapse: collapse; font-size: .875rem; }
th, td { text-align: right; padding: .75rem .5rem; border-bottom: 1px solid #e3ebe6; white-space: nowrap; }
th:first-child { text-align: left; min-width: 16rem; max-width: 32rem; white-space: normal; overflow-wrap: anywhere; font-weight: 500; }
thead th { background: #f2f6f3; }
thead button { border: 0; background: transparent; padding: .25rem; font-size: .875rem; }
.empty { padding: 1rem 0; color: #52665a; }
.notice { padding: .75rem 1rem; background: #fff2dc; border-radius: .5rem; font-size: .875rem; }
.error { color: #923322; }
.explanation { margin: 1.5rem 0; color: #466254; }
@media (max-width: 40rem) { .trend-panel { padding: 1rem; } .chart-canvas { min-width: max(20rem, calc(var(--minute-count) * 1.75rem)); } .chart-axis { font-size: .75rem; } .chart-scroll-hint { display: block; color: #52665a; } .chart-picker { width: 100%; } .minute-detail { padding: .75rem; } .performance-view { padding: 1rem; } .page-header { align-items: flex-start; } .toggle { width: 100%; } input[type="search"] { width: 100%; } }
</style>
